import React, { FC, useState } from "react";
import { useSelector } from "react-redux";
import { cloneDeep, set } from "lodash";
import { Box, Checkbox, Modal } from "@mui/material";

import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import CtaComponent from "../../../components/reusable-ui/cta";

import {
  CTA_SIZE,
  CTA_TYPE,
  DATE_TIME_FORMATS,
  MESSAGE_TYPE,
} from "../../../data/constants";
import { RootState } from "../../../redux/store";
import "./style.scss";
import { AGGREGATOR_TYPE } from "../../../data/outlet-status-constants";
import { useDispatch } from "react-redux";
import { uploadReconFile } from "../../../redux/slices";
import { getAggregatorLogo } from "../../../helpers/accounting-helper";
import moment, { Moment } from "moment";
import DateRangePicker from "../../../components/reusable-ui/date-range-picker";

interface Props {
  openStateModal: any;
  onModalClose: any;
}

const FileUploadModal: FC<Props> = (props: Props) => {
  const { onModalClose, openStateModal } = props;
  const [payload, setPayload] = useState<any>({
    aggregatorType: "",
    branchId: "",
    channelDefId: "",
    file: "",
    dateRange: {
      startDate: "",
      endDate: "",
    },
  });
  const { businessBranchesDataMap, allChannelsDefDataMap } = useSelector(
    (state: RootState) => ({
      businessBranchesDataMap: state.businessReducer.businessBranchesDataMap,
      allChannelsDefDataMap: state.businessReducer.allChannelsDefDataMap,
    })
  );
  const enableUploadCta =
    payload?.branchId &&
    payload?.channelDefId &&
    payload?.dateRange?.startDate &&
    payload?.dateRange?.endDate &&
    payload?.aggregatorType &&
    payload?.file;
  const dispatch = useDispatch();
  const PAYLOAD_KEYS = [
    "aggregatorType",
    "branchId",
    "channelDefId",
    "file",
    "startDate",
    "endDate",
  ];
  const DROPDOWN_CONFIGS = {
    branchId: {
      label: "Branch",
      optionsMap: businessBranchesDataMap,
      placeholder: "Select Branch",
      uniqueKey: "branchId",
      dropdownHeading: "Branches",
      searchPlaceholder: "Search a branch",
      parentClass: "w-full mb-6",
      key: "branchId",
      getListItemText: (data: any) => data?.branchName,
    },
    channelDefId: {
      label: "Channel",
      optionsMap: allChannelsDefDataMap,
      placeholder: "Select Channel",
      uniqueKey: "channelDefId",
      dropdownHeading: "Channels",
      searchPlaceholder: "Search a channel",
      parentClass: "w-full mb-6",
      key: "channelDefId",
      getListItemText: (data: any) => data?.name,
    },
  };

  const ERROR_MSG_MAP: any = {
    REST_ERR_INVALID_FILE_DATA_FORMAT: "Invalid date range selected",
    REST_ERR_INVALID_FILE_TYPE: "File type is invalid",
    REST_ERR_EMPTY_FILE: "Uploaded file is empty",
  };

  const handleInputChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(payload);
    set(dataToUpdate, fieldToUpdate, value);
    setPayload(dataToUpdate);
  };

  const onUploadFileCtaClick = () => {
    const callback = (response: any) => {
      if (!response?.code && !response?.message) {
        openStateModal({
          show: true,
          type: MESSAGE_TYPE.success,
          mainText: `You are good to go!`,
          subText: `The file has been successfully uploaded. Now you can check
            your reconciliation report by choosing the same branch, online partner and date range on the dashboard`,
        });
        onModalClose();
      } else {
        const errorCode = response?.message || response?.code;
        openStateModal({
          show: true,
          type: MESSAGE_TYPE.error,
          mainText: ERROR_MSG_MAP[errorCode] || errorCode,
        });
      }
    };
    let dataToSend = cloneDeep(payload);
    dataToSend = { ...dataToSend, ...dataToSend?.dateRange };
    const formData = new FormData();
    PAYLOAD_KEYS.forEach((key: string) =>
      formData.append(key, dataToSend?.[key])
    );
    const dataToPost = formData;
    dispatch(uploadReconFile({ dataToPost, callback }));
  };

  /**
   * The dates more than 3 years will always be disabled
   * @param current
   */
  const disabledDate = (current: any) => {
    return (
      current &&
      (current >= moment().endOf("day") || moment().diff(current, "days") > 364)
    );
  };

  const onDateRangeSubmission = (data: {
    startDate: Moment;
    endDate: Moment;
  }) => {
    handleInputChange(data, "dateRange");
  };

  function renderCustomDropDown(dropdownConfig: {
    label?: string;
    optionsMap: any;
    placeholder?: string;
    getListItemText?: any;
    parentClass?: string;
    dropdownHeading?: string;
    searchPlaceholder?: string;
    uniqueKey: any;
    key: string;
  }): any {
    return (
      <CustomDropdown
        textBoxLabel={dropdownConfig.label}
        optionsMap={dropdownConfig.optionsMap}
        isMultiSelect={false}
        placeholder={dropdownConfig.placeholder}
        getListItemText={dropdownConfig.getListItemText}
        parentClass={dropdownConfig.parentClass}
        dropdownHeading={dropdownConfig.dropdownHeading}
        searchPlaceholder={dropdownConfig.searchPlaceholder}
        onSubmit={(value: any) =>
          handleInputChange(value[0], dropdownConfig.key)
        }
        preSelectedValues={payload[dropdownConfig.key]}
        uniqueKey={dropdownConfig.uniqueKey}
        isMandatory={true}
      ></CustomDropdown>
    );
  }

  function renderAggregatorCheckbox(config: {
    id: string;
    label: string;
    parentClass: string;
  }) {
    const { id, label, parentClass } = config;
    let isSelected = payload?.aggregatorType === id;
    return (
      <div
        className={`flex items-center border rounded-[8px] px-18 py-[9px] cursor-pointer
          ${parentClass || ""}
          ${isSelected ? "border-[#2A77F2]" : "border-[#DEDEDF]"}
        `}
        onClick={() => handleInputChange(id, "aggregatorType")}
      >
        <Checkbox
          checked={isSelected}
          size="small"
          inputProps={{ "aria-label": "controlled" }}
          className="!m-0 !p-0"
        />
        <p className="text-14 font-medium leading-none mx-10">{label}</p>
        <img src={getAggregatorLogo(id)} alt="" width={20} height={20} />
      </div>
    );
  }

  function renderFileChooser(): React.JSX.Element {
    return (
      <>
        <label
          className={`bg-[#397EF5] bg-opacity-10 mb-8 rounded-[8px] w-full p-8
          custom-file-upload flex items-center justify-center cursor-pointer
          border border-[#C5DBFF]
        `}
        >
          {payload?.file?.name ? (
            <div className="flex items-center justify-between w-full px-[20px]">
              <p className="text-16 text-blue font-semibold m-0">
                {payload?.file?.name}
              </p>
              <p className="text-16 text-black font-semibold m-0">Edit</p>
            </div>
          ) : (
            <p className="text-16 text-blue font-semibold m-0">
              + Attach Payout File
            </p>
          )}
          <input
            style={{ display: "none" }}
            type="file"
            id={"uploadImage"}
            multiple={false}
            className="upload-file-input"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={(e: any) => handleInputChange(e.target.files[0], "file")}
          />
        </label>
        <p className="text-12 text-black/40">
          Files supported: CSV, Xlsx | Files Size: less than 10MB
        </p>
      </>
    );
  }

  return (
    <Modal open={true} onClose={onModalClose}>
      <Box className="recon-file-upload-modal modal-center w-[502px] h-[80vh]">
        <div className="modal-header flex items-center justify-between">
          <p className="text-19 font-bold">Upload New File</p>
          <div
            className="rista-sprite ic-cross-black"
            onClick={onModalClose}
          ></div>
        </div>
        <div className="modal-body-content px-20">
          {renderCustomDropDown(DROPDOWN_CONFIGS.branchId)}
          {renderCustomDropDown(DROPDOWN_CONFIGS.channelDefId)}
          <>
            <p className="text-14 text-[#656565] mb-8">
              Online Partner <span className="text-[#9A3131]">*</span>
            </p>
            <div className="flex items-center mb-32">
              {renderAggregatorCheckbox({
                ...AGGREGATOR_TYPE.swiggy,
                parentClass: "mr-6",
              })}
              {renderAggregatorCheckbox({
                ...AGGREGATOR_TYPE.zomato,
                parentClass: "ml-6",
              })}
            </div>
          </>
          <div className="mb-32">
            <p className="text-14 text-[#656565] mb-8">
              Date Range <span className="text-[#9A3131]">*</span>
            </p>
            <DateRangePicker
              dateFormat={DATE_TIME_FORMATS.dayMonthYear}
              disabledDate={disabledDate}
              timeDifference={365}
              errorMessage={`You can select date range max upto 90 days!`}
              onSubmit={onDateRangeSubmission}
              defaultDateRange={[null, null]}
            ></DateRangePicker>
          </div>

          {renderFileChooser()}
          <div className="modal-footer">
            <CtaComponent
              ctaSize={CTA_SIZE.medium}
              ctaText={"Upload File"}
              ctaType={CTA_TYPE.active_cta}
              onCtaClick={onUploadFileCtaClick}
              customClass={"w-full"}
              isDisabled={!enableUploadCta}
            ></CtaComponent>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default FileUploadModal;
